import { Ease, Tween } from "@westpierstudio/luma/createjs";
import { AssetSpec, ParticleEmitterAssetSpec, SpriteAssetSpec } from "@westpierstudio/luma/factory/PixiFactory";
import { DefaultLayout } from "@westpierstudio/luma/layout";
import { View } from "@westpierstudio/luma/mvc";
import { Resettable } from "@westpierstudio/luma/utils/reset";
import { GameServices } from "game/GameServices";
import { Emitter, Color } from "pixi-particles";
import { Container, settings, Sprite, Texture } from "pixi.js";
import { getSoundPlayer } from "sound/SoundLocator";
import { string2hex } from "@pixi/utils";
import { Signal } from "type-signals";
import { devLog } from "utils/Misc";

interface TransitionSettings {
    leafStartFrequency: number
    leafMainFrequency: number
    leafMainFrequencyDark: number
    leafEndFrequency: number
    leafStartTime: number
    leafStartDelay: number
    leafMainTime: number
    leafMainTimeDark: number
    leafEndTime: number
    leafBgFadeInTime: number
    leafBgWaitTime: number
    leafBgFadeOutTime: number
    leafBgColor: string
    leafTransitionGameDelay: number
    leafTransitionSkipDelay: number
    leafStartToCoveredTime: number
    leafMinimumSkipTime: number
    blackFadeInTime: number
    blackFadeOutTime: number
    blackWaitTime: number
}

interface TransitionSpec extends AssetSpec {
    settings: TransitionSettings
    transitionParticles: ParticleEmitterAssetSpec
    transitionParticlesDark: ParticleEmitterAssetSpec
    colorCycle: { time: number, color: Color }[]
}

class TransitionSignals {
    leafComplete = new Signal();
    fadeComplete = new Signal();
}

export class Transition extends View implements Resettable {
    private readonly spec: TransitionSpec;
    private readonly particles: Emitter;
    private readonly particlesDark: Emitter;
    private readonly particleContainer: Container;
    private readonly sprite: Sprite;

    private readonly BG_COLOUR = 0x000000;
    private readonly LEAF_BG_COLOUR;

    signals = new TransitionSignals();

    private startTime?: number;
    private leafTweens: createjs.Tween[] = [];
    private fadeTween?: createjs.Tween;

    constructor(protected override readonly services: GameServices, protected readonly layout: DefaultLayout) {
        super(services);

        const { factory, resources } = this.services;

        this.spec = resources.getSpec(`transition`) as TransitionSpec;
        this.sprite = factory.createSpriteFromTexture(Texture.WHITE, {}, this);
        this.sprite.tint = this.BG_COLOUR;
        this.sprite.visible = false;
        this.particleContainer = new Container();
        this.addChild(this.particleContainer);
        this.particlesDark = factory.createParticleEmitter(this.spec.transitionParticlesDark, this.particleContainer);
        this.particles = factory.createParticleEmitter(this.spec.transitionParticles, this.particleContainer);
        this.particles.emit = false;
        this.particlesDark.emit = false;
        this.LEAF_BG_COLOUR = string2hex(this.spec.settings.leafBgColor);

        this.resize();
        layout.onResize.add(() => this.resize());
    }

    doLeafTransition(onFadeCallback?: () => void) {
        const { particles, particlesDark } = this;
        const { leafMainTime, leafMainTimeDark, leafMainFrequency, leafMainFrequencyDark, leafStartDelay, leafTransitionGameDelay, leafStartToCoveredTime } = this.spec.settings;
        particlesDark.emit = true;
        this.startTime = Date.now();
        getSoundPlayer().transition();
        const tweenDark = this.tweenParticles(particlesDark, leafMainTimeDark, leafMainFrequencyDark, true, onFadeCallback);
        this.startColorCycle(particlesDark, this.spec.colorCycle);
        const tweenCycle = Tween.get(this).wait(leafStartDelay).call(() => {
            particles.emit = true;
            this.startColorCycle(particles, this.spec.colorCycle);
            this.leafTweens.push(this.tweenParticles(particles, leafMainTime, leafMainFrequency, false, onFadeCallback));
        });
        const tweenWait = Tween.get(this).wait(leafStartToCoveredTime).wait(leafTransitionGameDelay).call(() => {
            this.signals.leafComplete.dispatch();
            this.startTime = undefined;
        });
        this.leafTweens.push(tweenDark, tweenCycle, tweenWait);
    }

    skipLeafTransition() {
        if (this.startTime) {
            const timeSinceStart = (Date.now() - this.startTime) * this.services.application.ticker.speed;
            const { leafMainFrequency, leafMainFrequencyDark, leafStartToCoveredTime,
                leafMinimumSkipTime, leafTransitionSkipDelay } = this.spec.settings;
            if (timeSinceStart < leafStartToCoveredTime - leafMinimumSkipTime) {
                const timeSkip = leafStartToCoveredTime - timeSinceStart;
                this.startTime -= timeSkip;
                this.particles.emit = true;
                this.particlesDark.emit = true;
                this.particles.frequency = leafMainFrequency;
                this.particlesDark.frequency = leafMainFrequencyDark;
                const col = this.spec.colorCycle[0].color;
                this.particles.startColor.value = col;
                this.particlesDark.startColor.value = col;
                this.particles.update(timeSkip);
                this.particlesDark.update(timeSkip);
                for (const tween of this.leafTweens) {
                    tween.setPosition(leafStartToCoveredTime);
                }
            }
            else {
                this.startTime = undefined;
                this.particles.emit = false;
                this.particlesDark.emit = false;
                this.particles.cleanup();
                this.particlesDark.cleanup();
                if (this.fadeTween) {
                    this.fadeTween.timeScale = Infinity;
                }
                Tween.removeTweens(this);
                Tween.removeTweens(this.particles);
                Tween.removeTweens(this.particlesDark);
                Tween.get(this)
                    .wait(leafTransitionSkipDelay)
                    .call(() => {
                        this.signals.leafComplete.dispatch();
                    });
                getSoundPlayer().stopTransition();
            }
        }
    }

    private tweenParticles(emitter: Emitter, leafMainTime: number, leafMainFrequency: number, doFade: boolean, onFadeCallback?: (() => void)) {
        const { leafStartFrequency, leafStartTime, leafBgFadeInTime, leafBgFadeOutTime,
            leafBgWaitTime, leafEndFrequency, leafEndTime } = this.spec.settings;
        const t = Tween.get(emitter)
            .to({ frequency: leafStartFrequency }, 0)
            .to({ frequency: leafMainFrequency }, leafStartTime, Ease.quartIn)
            .wait(leafMainTime)
            .call(() => {
                if (doFade) {
                    this.doFade(leafBgFadeInTime, leafBgWaitTime, leafBgFadeOutTime, this.LEAF_BG_COLOUR, onFadeCallback);
                }
            })
            .wait(leafBgWaitTime)
            .to({ frequency: leafEndFrequency }, 0)
            .wait(leafEndTime / 2)
            .to({ frequency: leafStartFrequency }, 0)
            .wait(leafEndTime / 2)
            .call(() => {
                emitter.emit = false;
                Tween.removeTweens(emitter);
            });
        return t;
    }

    startColorCycle(particles: Emitter, colorCycle: { time: number; color: Color; }[]) {
        const cycle = Tween.get(particles);
        for (const value of colorCycle) {
            cycle
                .call(() => {
                    particles.startColor.value = value.color;
                })
                .wait(value.time);
        }
        cycle.loop = -1;
    }

    doTransitionToBase(onFadeCallback?: () => void) {
        const { blackFadeInTime, blackFadeOutTime, blackWaitTime } = this.spec.settings;
        this.doFade(blackFadeInTime, blackWaitTime, blackFadeOutTime, this.BG_COLOUR, onFadeCallback);
    }

    doFade(timeIn: number, timeWait: number, timeOut: number, tint: number = this.LEAF_BG_COLOUR, onFade?: () => void) {
        this.sprite.visible = true;
        this.sprite.tint = tint;
        this.fadeTween = Tween.get(this.sprite)
            .to({ alpha: 0 }, 0)
            .to({ alpha: 1 }, timeIn, Ease.quadOut)
            .call(() => {
                if (onFade) {
                    onFade();
                }
                this.leafTweens = [];
            })
            .wait(timeWait)
            .to({ alpha: 0 }, timeOut, Ease.quadIn)
            .call(() => {
                this.sprite.visible = false;
                this.fadeTween = undefined;
                this.signals.fadeComplete.dispatch();
            });
    }

    private resize(): void {
        const bounds = this.layout.getScreenBoundaryInBucketSpace();
        const safety = 4;
        this.sprite.x = bounds.left - (safety / 2);
        this.sprite.y = bounds.top - (safety / 2);
        this.sprite.width = bounds.width + safety;
        this.sprite.height = bounds.height + safety;
        this.particleContainer.position = this.sprite.position;
        const scale = (this.sprite.scale.x > this.sprite.scale.y ? this.sprite.scale.x : this.sprite.scale.y) / 100 * 4 / this.layout.getTextureScale();
        this.particleContainer.scale = { x: scale, y: scale };
        this.particles.resetPositionTracking();
        this.particlesDark.resetPositionTracking();
    }

    override reset(): void {
        Tween.removeTweens(this);
        Tween.removeTweens(this.sprite);
        Tween.removeTweens(this.particles);
        this.sprite.alpha = 0;
        this.sprite.visible = false;
        this.particles.emit = false;
        this.particles.cleanup();
        this.particlesDark.emit = false;
        this.particlesDark.cleanup();
    }
}